/**
 * What a thread asks of the rest of Activity — `docs/activity.md` §6.5, §7.1,
 * §9: **Move** / *Reschedule today's visit* open the item's own sheet (folder
 * 48's book mode for a site visit, the meeting sheet in edit mode), and
 * **Make** opens a New sheet attached as the thread is.
 */
import type { ConversationThread } from '@rowted/shared'
import { openNewRequest } from '../../requests/open-request.js'
import { activityRequest } from '../api.js'
import { openActivityNew } from '../calendar/overlays.js'

export type MakeKind = 'task' | 'meeting' | 'call' | 'note'

/** What a New sheet opened from a thread starts from (§9.2): its records, and where it came from. */
export function threadPrefill(thread: ConversationThread): Record<string, unknown> {
  return {
    links: thread.links.map((link) => ({ type: link.type, id: link.id, label: link.label })),
    fromConversationId: thread.id,
    ...(thread.row.contactId ? { contactId: thread.row.contactId } : {}),
  }
}

/**
 * **Make** (§7.1). *Log a call* has no New ▾ kind of its own yet (§11.5's
 * sheet), so it asks for the Note sheet marked as a call.
 */
export function makeFromThread(kind: MakeKind, thread: ConversationThread): void {
  if (kind === 'call') {
    openActivityNew({ kind: 'note', prefill: { ...threadPrefill(thread), logCall: true } })
    return
  }
  openActivityNew({ kind, prefill: threadPrefill(thread) })
}

/** **Move** / *Reschedule today's visit* — §6.5's **Reschedule**. */
export async function moveItem(kind: string, id: string): Promise<void> {
  if (kind === 'appointment') {
    try {
      const card = await activityRequest<{ id: string; job: { id: string } | null }>(`/activity/stops/${id}`)
      if (card.job) {
        openNewRequest({ book: { jobId: card.job.id, appointmentId: card.id } })
        return
      }
    } catch {
      /* Falls through to the meeting sheet's edit mode, which can move any timed item. */
    }
  }
  openActivityNew({ kind: kind === 'task' ? 'task' : 'meeting', prefill: { editId: id } })
}

/*
 * The conversation open on the Inbox tab, so **New ▾** there starts from it
 * (§9.2 — *opened from Dana Okafor’s thread*, Board 11 D and F).
 */
let openThread: ConversationThread | null = null
export function setOpenThread(thread: ConversationThread | null): void {
  openThread = thread
}
export function openThreadPrefill(): Record<string, unknown> | null {
  return openThread ? threadPrefill(openThread) : null
}
